import React, { useState, useRef, useEffect } from 'react';
import { Play, Pause, Video } from 'lucide-react';
import { processVideoUrl, isValidVideoUrl } from '../utils/videoUtils';

interface ProductVideoPreviewProps {
  imageUrl: string;
  videoUrl?: string | null;
  alt: string;
  className?: string;
}

const ProductVideoPreview: React.FC<ProductVideoPreviewProps> = ({
  imageUrl,
  videoUrl,
  alt,
  className = ''
}) => {
  const [isHovered, setIsHovered] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [videoError, setVideoError] = useState(false);
  const [videoLoading, setVideoLoading] = useState(false);
  const videoRef = useRef<HTMLVideoElement>(null);
  const iframeRef = useRef<HTMLIFrameElement>(null);

  // Reset video error when videoUrl changes
  useEffect(() => {
    setVideoError(false);
    setVideoLoading(false);
  }, [videoUrl]);

  // Process video URL
  const videoInfo = videoUrl ? processVideoUrl(videoUrl) : null;
  const hasValidVideo = videoInfo && videoInfo.type !== 'invalid' && !videoError;

  // Get the appropriate image to show (video thumbnail or fallback to product image)
  const getDisplayImage = (): string => {
    if (videoInfo?.thumbnail) {
      return videoInfo.thumbnail as string;
    }
    return imageUrl;
  };

  // Enhanced debug logging
  useEffect(() => {
    if (videoUrl) {
      console.log('🔍 ProductVideoPreview Debug:', { 
        productName: alt,
        videoUrl,
        hasVideoUrl: Boolean(videoUrl),
        videoType: videoInfo?.type,
        hasValidVideo,
        videoError,
        isMobile
      });
    }
  }, [imageUrl, videoUrl, alt, videoInfo?.type, hasValidVideo, videoError, isMobile]);

  // Check if device is mobile (no hover support)
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.matchMedia('(hover: none)').matches);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Debug iframe rendering
  useEffect(() => {
    if (hasValidVideo && (videoInfo?.type === 'youtube' || videoInfo?.type === 'vimeo') && typeof videoInfo.embedUrl === 'string') {
      console.log('🎬 Iframe should render:', { 
        productName: alt,
        isHovered, 
        isMobile, 
        embedUrl: isHovered && !isMobile ? videoInfo.embedUrl : 'not loaded yet'
      });
    }
  }, [hasValidVideo, videoInfo, isHovered, isMobile, alt]);

  // Handle video play/pause
  useEffect(() => {
    if (!videoUrl || !hasValidVideo) {
      console.log('❌ Skipping video play/pause:', { 
        productName: alt,
        hasVideoUrl: Boolean(videoUrl), 
        hasValidVideo 
      });
      return;
    }

    console.log('🎬 Video play/pause effect:', { 
      productName: alt,
      isHovered, 
      isMobile, 
      videoType: videoInfo?.type 
    });

    if (isHovered && !isMobile) {
      setVideoLoading(true);
      
      if (videoInfo?.type === 'direct' && videoRef.current) {
        console.log('🎥 Playing direct video:', { productName: alt });
        videoRef.current.play().then(() => {
          setIsPlaying(true);
          setVideoLoading(false);
          console.log('✅ Direct video started successfully:', { productName: alt });
        }).catch((error) => {
          console.error('❌ Direct video play error:', { productName: alt, error });
          setVideoError(true);
          setVideoLoading(false);
          setIsHovered(false);
        });
      } else if ((videoInfo?.type === 'youtube' || videoInfo?.type === 'vimeo') && iframeRef.current) {
        console.log('📺 Loading embedded video:', { productName: alt, videoType: videoInfo.type });
        setIsPlaying(true);
        setVideoLoading(false);
      }
    } else {
      if (videoInfo?.type === 'direct' && videoRef.current) {
        videoRef.current.pause();
      }
      setIsPlaying(false);
    }
  }, [isHovered, videoUrl, isMobile, videoError, hasValidVideo, videoInfo, alt]);

  // Handle mouse events
  const handleMouseEnter = () => {
    console.log('🖱️ Mouse Enter:', { 
      productName: alt,
      isMobile, 
      hasValidVideo, 
      videoType: videoInfo?.type 
    });
    if (!isMobile && hasValidVideo) {
      setIsHovered(true);
    }
  };

  const handleMouseLeave = () => {
    console.log('🖱️ Mouse Leave:', { productName: alt });
    if (!isMobile) {
      setIsHovered(false);
    }
  };

  // Handle mobile tap to play
  const handleMobileTap = () => {
    if (isMobile && hasValidVideo) {
      if (isPlaying) {
        if (videoInfo?.type === 'direct' && videoRef.current) {
          videoRef.current.pause();
        }
        setIsPlaying(false);
      } else {
        setVideoLoading(true);
        if (videoInfo?.type === 'direct' && videoRef.current) {
          videoRef.current.play().then(() => {
            setIsPlaying(true);
            setVideoLoading(false);
          }).catch((error) => {
            console.error('Video play error:', error);
            setVideoError(true);
            setVideoLoading(false);
          });
        } else {
          // For embedded videos, just show as playing
          setIsPlaying(true);
          setVideoLoading(false);
        }
      }
    }
  };

  return (
    <div 
      className={`relative overflow-hidden ${className}`}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      onClick={handleMobileTap}
      style={{ border: '1px solid red' }}
    >
      {/* Static Image - Always show thumbnail for YouTube/Vimeo, fallback for direct videos */}
      <img
        src={getDisplayImage()}
        alt={alt}
        className={`w-full h-full object-cover transition-opacity duration-300 ${
          isHovered && hasValidVideo && !isMobile && videoInfo?.type === 'direct' ? 'opacity-0' : 'opacity-100'
        }`}
      />

      {/* Direct Video Element */}
      {hasValidVideo && videoInfo?.type === 'direct' && videoUrl && (
        <video
          ref={videoRef}
          src={videoUrl}
          className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-300 ${
            isHovered && !isMobile ? 'opacity-100' : 'opacity-0'
          }`}
          muted
          loop
          playsInline
          preload="metadata"
          onError={() => setVideoError(true)}
        />
      )}

      {/* Embedded Video (YouTube/Vimeo) - Show on hover */}
      {hasValidVideo && (videoInfo?.type === 'youtube' || videoInfo?.type === 'vimeo') && typeof videoInfo.embedUrl === 'string' && (
        <iframe
          ref={iframeRef}
          src={isHovered && !isMobile && videoInfo.embedUrl ? videoInfo.embedUrl : undefined}
          className={`absolute inset-0 w-full h-full transition-opacity duration-300 ${
            isHovered && !isMobile ? 'opacity-100' : 'opacity-0'
          }`}
          frameBorder="0"
          allow="autoplay; encrypted-media; picture-in-picture"
          allowFullScreen
          onError={() => setVideoError(true)}
          onLoad={() => {
            console.log('✅ Embedded video iframe loaded successfully:', { 
              productName: alt,
              videoType: videoInfo.type,
              embedUrl: videoInfo.embedUrl 
            });
            setVideoLoading(false);
          }}
        />
      )}

      {/* Loading Indicator */}
      {videoLoading && (
        <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-30">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-white"></div>
        </div>
      )}

      {/* Mobile Play Button Overlay */}
      {isMobile && hasValidVideo && !isPlaying && !videoLoading && (
        <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-30">
          <div className="bg-white bg-opacity-90 rounded-full p-3">
            <Play size={24} className="text-gray-800" />
          </div>
        </div>
      )}

      {/* Mobile Pause Button Overlay */}
      {isMobile && hasValidVideo && isPlaying && (
        <div className="absolute top-2 right-2">
          <div className="bg-black bg-opacity-50 rounded-full p-2">
            <Pause size={16} className="text-white" />
          </div>
        </div>
      )}

      {/* Desktop Play Indicator (always visible for YouTube/Vimeo, hover for direct) */}
      {!isMobile && hasValidVideo && (
        <div className={`absolute top-2 right-2 transition-opacity duration-300 ${
          (videoInfo?.type === 'youtube' || videoInfo?.type === 'vimeo') || isHovered ? 'opacity-100' : 'opacity-0'
        }`}>
          <div className="bg-black bg-opacity-50 rounded-full p-2">
            <Play size={16} className="text-white" />
          </div>
        </div>
      )}

      {/* Video Error Indicator */}
      {videoError && videoUrl && (
        <div className="absolute top-2 right-2">
          <div className="bg-red-500 bg-opacity-75 rounded-full p-2" title="Video failed to load">
            <Video size={16} className="text-white" />
          </div>
        </div>
      )}

      {/* Debug Info (only in development) */}
      {import.meta.env.DEV && videoUrl && (
        <div className="absolute bottom-2 left-2 bg-black bg-opacity-75 text-white text-xs p-1 rounded">
          {videoInfo?.type || 'unknown'}
        </div>
      )}
    </div>
  );
};

export default ProductVideoPreview; 